import { useCallback, useEffect, useState } from "react";
import { t } from "../i18n";
import { failureLabel, moduleName, stageLabel } from "../ui/labels";
import { diffEvents } from "./events";
import { emitStationEvents } from "./feed";
import { useEngine } from "./runtime";
import { useGameUi } from "./store";

/** Branche les événements de la station sur le fil (rendu) et sur les toasts (interface). */
export function useStationEvents(): void {
  const engine = useEngine();
  useEffect(() => {
    if (!engine) return;
    return engine.onSteps((previous, current) => {
      const events = diffEvents(previous, current);
      emitStationEvents(events);
      const ui = useGameUi.getState();
      const toast = ui.toast;
      if (events.levelUp !== null) ui.showLevelUp(events.levelUp);
      for (const event of events.modules) {
        const module = current.modules.find((candidate) => candidate.id === event.moduleId);
        if (!module) continue;
        const name = moduleName(module.type);
        switch (event.kind) {
          case "failure":
            if (module.failure) toast(t("event.failure", { module: name, failure: failureLabel(module.failure.kind) }), "warn");
            break;
          case "escalation":
            toast(t("event.escalation", { module: name, stage: stageLabel(event.stage) }), "alert");
            break;
          case "repaired":
            toast(t("event.repaired", { module: name }), "good");
            break;
          case "patched":
            toast(t("event.patched", { module: name }), "good");
            break;
          case "upgraded":
            toast(t("event.upgraded", { module: name, level: event.level }), "good");
            break;
          case "built":
            toast(t("event.built", { module: name }), "info");
            break;
        }
      }
    });
  }, [engine]);
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** Raccourcis : Échap pour annuler ou fermer, B construire, P pannes, V vaisseaux, S station. */
export function useShortcuts(): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || isTyping(event.target)) return;
      // Une fenêtre modale est ouverte : la station attend derrière.
      if (document.querySelector('[aria-modal="true"]')) return;
      const ui = useGameUi.getState();
      if (event.key === "Escape") {
        if (ui.buildType) ui.cancelBuild();
        else if (ui.selectedShip) ui.selectShip(null);
        else if (ui.selection) ui.select(null);
        else if (ui.panel) ui.openPanel(null);
        return;
      }
      const panels = { b: "build", p: "failures", v: "ships", s: "station" } as const;
      const panel = panels[event.key.toLowerCase() as keyof typeof panels];
      if (panel) ui.togglePanel(panel);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}

export interface ElementSize {
  width: number;
  height: number;
}

/** Taille d'un élément, suivie quand elle change (fronton, pupitre et panneau, tiroir du téléphone). */
export function useElementSize(): [(element: HTMLElement | null) => (() => void) | undefined, ElementSize] {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });
  const ref = useCallback((element: HTMLElement | null) => {
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() =>
      setSize((previous) =>
        previous.width === element.offsetWidth && previous.height === element.offsetHeight
          ? previous
          : { width: element.offsetWidth, height: element.offsetHeight },
      ),
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
      setSize({ width: 0, height: 0 });
    };
  }, []);
  return [ref, size];
}
